import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { activateLicense, isValidKeyFormat } from '../licenseUtils';

export default function SignUpPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = create account, 2 = enter key
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      });

      if (authError) throw authError;

      // Create user profile with default technician role
      if (data?.user) {
        await supabase.from('users').upsert({
          auth_user_id: data.user.id,
          email: email.toLowerCase(),
          full_name: fullName,
          role: 'technician', // Default — will be overridden by license key
        }, { onConflict: 'auth_user_id' });
      }

      setStep(2);
    } catch (err) {
      setError(err.message || 'Failed to create account.');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateKey = async (e) => {
    e.preventDefault();
    setError('');

    const cleanKey = licenseKey.trim().toUpperCase();
    if (!isValidKeyFormat(cleanKey)) {
      setError('Invalid key format. Expected: XX-XXXX-XXXX-XXXX');
      return;
    }

    setLoading(true);
    try {
      const result = await activateLicense(cleanKey, email, 'forge-casino');
      if (result.success) {
        setSuccess(`Account activated! Role: ${result.assignedRole || 'assigned'}. Redirecting to login...`);
        // Sign out so they go through proper onboarding
        await supabase.auth.signOut();
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Activation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-format license key as user types
  const handleKeyInput = (val) => {
    const raw = val.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    let formatted = '';
    for (let i = 0; i < raw.length && i < 14; i++) {
      if (i === 2 || i === 6 || i === 10) formatted += '-';
      formatted += raw[i];
    }
    setLicenseKey(formatted);
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: 'var(--bg)',
    border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        width: '100%', maxWidth: 420, padding: 40, background: 'var(--card)',
        borderRadius: 16, border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, background: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 28, fontWeight: 700, color: '#fff',
          }}>
            {('Forge Casino').charAt(0)}
          </div>
          <h1 style={{ color: 'var(--text)', fontSize: 24, fontWeight: 700, margin: '0 0 4px' }}>
            {step === 1 ? 'Create Account' : 'Activate License'}
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            {step === 1 ? 'Set up your account to get started' : 'Enter the registration key from your company'}
          </p>
          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--primary)' }} />
            <div style={{ width: 40, height: 4, borderRadius: 2, background: step >= 2 ? 'var(--primary)' : 'var(--border)' }} />
          </div>
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', marginBottom: 20, borderRadius: 8,
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#ef4444', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '10px 14px', marginBottom: 20, borderRadius: 8,
            background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
            color: '#10b981', fontSize: 13,
          }}>
            {success}
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleCreateAccount}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Full Name
              </label>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="John Smith" required style={inputStyle} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Email Address
              </label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required style={inputStyle} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Password
              </label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" required style={inputStyle} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Confirm Password
              </label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required style={inputStyle} />
            </div>
            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '12px 0', background: 'var(--primary)', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
            }}>
              {loading ? 'Creating account...' : 'Next →'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleActivateKey}>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Registration Key
              </label>
              <input
                type="text"
                value={licenseKey}
                onChange={(e) => handleKeyInput(e.target.value)}
                placeholder="XX-XXXX-XXXX-XXXX"
                required
                style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 18, textAlign: 'center', letterSpacing: 2 }}
              />
              <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>
                Your company administrator will provide this key.
              </p>
            </div>
            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '12px 0', background: 'var(--primary)', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
            }}>
              {loading ? 'Activating...' : 'Activate & Continue'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <span
            onClick={() => navigate('/login')}
            style={{ color: 'var(--primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >
            Already have an account? Sign In
          </span>
        </div>

        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11, marginTop: 24, marginBottom: 0 }}>
          Powered by Miskulin Technologies
        </p>
      </div>
    </div>
  );
}
